import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Mail,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Bell,
  UserPlus,
  RefreshCw,
  Calendar,
  BarChart3,
} from "lucide-react";
import { PiktableLoader } from "@/components/PiktableLoader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAdminEmailStats, AdminEmailStatsParams } from "@/hooks/useAdminEmailStats";

const EMAIL_TYPE_LABELS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  confirmation: { label: "Confirmation", color: "bg-green-500/10 text-green-600", icon: <CheckCircle className="w-3 h-3" /> },
  modification: { label: "Modification", color: "bg-blue-500/10 text-blue-600", icon: <Mail className="w-3 h-3" /> },
  cancellation: { label: "Annulation", color: "bg-red-500/10 text-red-600", icon: <XCircle className="w-3 h-3" /> },
  reminder: { label: "Rappel J-1", color: "bg-purple-500/10 text-purple-600", icon: <Bell className="w-3 h-3" /> },
  followup: { label: "Suivi post-visite", color: "bg-amber-500/10 text-amber-600", icon: <Mail className="w-3 h-3" /> },
  onboarding_followup_j3: { label: "Onboarding J+3", color: "bg-orange-500/10 text-orange-600", icon: <UserPlus className="w-3 h-3" /> },
  onboarding_followup_j7: { label: "Onboarding J+7", color: "bg-orange-500/10 text-orange-600", icon: <UserPlus className="w-3 h-3" /> },
  welcome: { label: "Bienvenue", color: "bg-indigo-500/10 text-indigo-600", icon: <UserPlus className="w-3 h-3" /> },
};

const CHART_COLORS: Record<string, string> = {
  confirmation: "#22c55e",
  modification: "#3b82f6",
  cancellation: "#ef4444",
  reminder: "#a855f7",
  followup: "#f59e0b",
  onboarding_followup_j3: "#f97316",
  onboarding_followup_j7: "#ea580c",
  welcome: "#6366f1",
};

export default function AdminEmailStats() {
  const [period, setPeriod] = useState<AdminEmailStatsParams["period"]>("30d");
  const [emailType, setEmailType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [establishmentId, setEstablishmentId] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, refetch, isRefetching } = useAdminEmailStats({
    period,
    emailType: emailType || undefined,
    status: status || undefined,
    establishmentId: establishmentId || undefined,
    page,
    limit,
  });

  const totalPages = data ? Math.ceil(data.totalLogs / limit) : 1;

  const getEmailTypeBadge = (type: string) => {
    const config = EMAIL_TYPE_LABELS[type] || { label: type, color: "bg-gray-500/10 text-gray-600", icon: <Mail className="w-3 h-3" /> };
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    if (status === "sent") {
      return (
        <Badge className="bg-green-500/10 text-green-600 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Envoyé
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-500/10 text-red-600 flex items-center gap-1">
        <XCircle className="w-3 h-3" />
        Échec
      </Badge>
    );
  };

  // Get unique email types from the data for chart legend
  const emailTypes = data ? Object.keys(data.summary.byType) : [];

  if (isLoading) {
    return <PiktableLoader message="Chargement des statistiques email..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-serif font-bold flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-primary" />
                Statistiques emails
              </h1>
              <p className="text-muted-foreground">
                Vue d'ensemble des emails automatiques
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Total envoyés
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {data?.summary.total || 0}
              </div>
              <p className="text-sm text-muted-foreground">
                sur la période
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Confirmations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {data?.summary.byType.confirmation?.sent || 0}
              </div>
              <p className="text-sm text-muted-foreground">
                réservations
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Rappels
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {data?.summary.byType.reminder?.sent || 0}
              </div>
              <p className="text-sm text-muted-foreground">
                J-1
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Onboarding
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-600">
                {(data?.summary.byType.onboarding_followup_j3?.sent || 0) +
                  (data?.summary.byType.onboarding_followup_j7?.sent || 0)}
              </div>
              <p className="text-sm text-muted-foreground">
                J+3 & J+7
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Taux d'échec
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {data?.summary.total
                  ? ((data.summary.byStatus.failed / data.summary.total) * 100).toFixed(1)
                  : 0}%
              </div>
              <p className="text-sm text-muted-foreground">
                {data?.summary.byStatus.failed || 0} échecs
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Period Selector & Chart */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Évolution par type
              </CardTitle>
              <div className="flex gap-2">
                {(["7d", "30d", "90d"] as const).map((p) => (
                  <Button
                    key={p}
                    variant={period === p ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setPeriod(p);
                      setPage(1);
                    }}
                  >
                    {p === "7d" ? "7 jours" : p === "30d" ? "30 jours" : "90 jours"}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {data?.timeline && data.timeline.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) =>
                      format(new Date(value), "dd/MM", { locale: fr })
                    }
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip
                    labelFormatter={(value) =>
                      format(new Date(value as string), "d MMMM yyyy", { locale: fr })
                    }
                    formatter={(value: number, name: string) => [
                      value,
                      EMAIL_TYPE_LABELS[name]?.label || name,
                    ]}
                  />
                  <Legend
                    formatter={(value) => EMAIL_TYPE_LABELS[value]?.label || value}
                  />
                  {emailTypes.map((type) => (
                    <Bar
                      key={type}
                      dataKey={type}
                      stackId="emails"
                      fill={CHART_COLORS[type] || "#94a3b8"}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Aucune donnée pour cette période
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters & Table */}
        <Card>
          <CardHeader>
            <CardTitle>Détail des envois</CardTitle>
            <div className="flex flex-wrap gap-3 mt-4">
              <Select
                value={emailType}
                onValueChange={(v) => {
                  setEmailType(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type d'email" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {Object.entries(EMAIL_TYPE_LABELS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={status}
                onValueChange={(v) => {
                  setStatus(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="sent">Envoyé</SelectItem>
                  <SelectItem value="failed">Échec</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={establishmentId}
                onValueChange={(v) => {
                  setEstablishmentId(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Établissement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les établissements</SelectItem>
                  {data?.establishments.map((est) => (
                    <SelectItem key={est.id} value={est.id}>
                      {est.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(emailType || status || establishmentId) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEmailType("");
                    setStatus("");
                    setEstablishmentId("");
                    setPage(1);
                  }}
                >
                  Réinitialiser les filtres
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Établissement</TableHead>
                    <TableHead>Destinataire</TableHead>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.logs && data.logs.length > 0 ? (
                    data.logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap">
                          {format(new Date(log.sent_at), "d MMM yyyy, HH:mm", {
                            locale: fr,
                          })}
                        </TableCell>
                        <TableCell>{getEmailTypeBadge(log.email_type)}</TableCell>
                        <TableCell className="max-w-[150px] truncate">
                          {log.establishment_name}
                        </TableCell>
                        <TableCell>
                          <div className="max-w-[180px]">
                            <p className="font-medium truncate">{log.customer_name}</p>
                            <p className="text-sm text-muted-foreground truncate">
                              {log.customer_email}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {log.subject}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(log.status)}
                          {log.error_message && (
                            <p className="text-xs text-red-500 mt-1 max-w-[150px] truncate">
                              {log.error_message}
                            </p>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        Aucun email trouvé
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Page {page} sur {totalPages} ({data?.totalLogs} emails)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Précédent
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
